// =====================================================
// DASHBOARD STATS — Export CSV du dashboard (RUN 6)
// =====================================================
// Assemble les CHIFFRES AFFICHÉS du dashboard en lignes plates « long format »
// (Section | Indicateur | Valeur | N-1 | Δ) prêtes pour l'utilitaire CSV existant
// du CRM (`exportToCSV`, délimiteur « ; » + BOM Excel FR). Respecte la PLAGE DE
// DATES active (via les data déjà calculées) et le toggle N-1 (colonnes N-1/Δ
// vides si off). RETOUCHE 1 : plus de segment/période ni de masquage ID.
//
// Fonction PURE (retourne les lignes) → testable en vitest sans DOM. Le
// téléchargement (Blob) reste délégué à `exportToCSV`.

import type { ConversionData } from './conversion';
import type { AppelsData } from './appels';
import type { OriginData } from './origin';
import type { RomainData } from './romain';
import type { DeltaValue, GlobalKpis, KpiKey } from './types';
import { KPI_LABELS } from './nomenclature';

/** Une ligne plate d'export (clés = en-têtes CSV FR). */
export interface DashboardCsvRow {
  Section: string;
  Indicateur: string;
  Valeur: string;
  'N-1': string;
  Δ: string;
}

export interface DashboardCsvContext {
  kpis: GlobalKpis;
  conversion: ConversionData | null;
  appels: AppelsData | null;
  origin: OriginData | null;
  romain: RomainData | null;
  /** Toggle N-1 actif → remplit les colonnes N-1/Δ. */
  showN1: boolean;
  /** Libellé de la plage de dates active (ex. « 10/04/2026 → 09/07/2026 »). */
  rangeLabel: string;
  editionName: string;
}

function fmtNum(n: number): string {
  return n.toLocaleString('fr-FR');
}

/** Δ absolu + %, ou '' si N-1 indisponible / toggle off. */
function fmtDelta(d: DeltaValue, showN1: boolean): { n1: string; delta: string } {
  if (!showN1 || d.n1Unavailable || d.prevValue == null) return { n1: '', delta: '' };
  const abs = d.deltaAbs ?? 0;
  const sign = abs > 0 ? '+' : '';
  let pct: string;
  if (d.deltaPct == null) pct = '';
  else if (!isFinite(d.deltaPct)) pct = '+100%';
  else pct = `${d.deltaPct > 0 ? '+' : ''}${d.deltaPct.toFixed(1)}%`;
  return {
    n1: fmtNum(d.prevValue),
    delta: pct ? `${sign}${fmtNum(abs)} (${pct})` : `${sign}${fmtNum(abs)}`,
  };
}

/** Taux 0..1 → « 34,2 % » ou 'N/A'. */
function fmtRate(v: number | null): string {
  if (v == null) return 'N/A';
  return `${(v * 100).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`;
}

/**
 * Construit les lignes d'export CSV du dashboard à partir des données affichées.
 * Ordre = ordre de lecture de la page (KPI → conversion → appels → origine → Romain).
 */
export function buildDashboardCsvRows(ctx: DashboardCsvContext): DashboardCsvRow[] {
  const rows: DashboardCsvRow[] = [];
  const { showN1 } = ctx;

  const push = (section: string, indicateur: string, valeur: string, d?: DeltaValue) => {
    const { n1, delta } = d ? fmtDelta(d, showN1) : { n1: '', delta: '' };
    rows.push({ Section: section, Indicateur: indicateur, Valeur: valeur, 'N-1': n1, Δ: delta });
  };

  // ── Indicateurs globaux ────────────────────────────────────────────────────
  const KPI_ORDER: KpiKey[] = [
    'bpi',
    'pi',
    'id',
    'pr',
    'derogations',
    'derogations_acceptees',
    'pi_attente',
  ];
  for (const key of KPI_ORDER) {
    const kpi = ctx.kpis[key];
    push('Indicateurs globaux', KPI_LABELS[key], fmtNum(kpi.value), kpi);
  }

  // ── Conversion & taux ───────────────────────────────────────────────────────
  if (ctx.conversion) {
    const { funnel, editionRates } = ctx.conversion;
    push('Conversion', 'Taux BPI → ID', fmtRate(funnel.bpiToId.value));
    push('Conversion', 'Taux BPI → PI', fmtRate(funnel.bpiToPi.value));
    push('Conversion', 'Taux PI → ID', fmtRate(funnel.piToId.value));
    push('Taux édition', "Taux d'annulation", fmtRate(editionRates.annulation.value));
    push('Taux édition', 'Taux de report', fmtRate(editionRates.report.value));
  }

  // ── Suivi des appels (buckets 0/1/2/3+) ─────────────────────────────────────
  if (ctx.appels) {
    for (const b of ctx.appels.buckets) {
      push('Suivi des appels', `Relançables — ${b.label}`, fmtNum(b.total));
    }
  }

  // ── Origine des PI ──────────────────────────────────────────────────────────
  if (ctx.origin) {
    for (const s of ctx.origin.slices) {
      const d: DeltaValue | undefined =
        s.prevCount == null
          ? undefined
          : {
              value: s.count,
              prevValue: s.prevCount,
              deltaAbs: s.count - s.prevCount,
              deltaPct:
                s.prevCount === 0
                  ? s.count === 0
                    ? 0
                    : Infinity
                  : ((s.count - s.prevCount) / s.prevCount) * 100,
              n1Unavailable: false,
            };
      push('Origine des PI', s.label, fmtNum(s.count), d);
    }
  }

  // ── Tableau de Romain (mensuel, par métrique × mois) ───────────────────────
  if (ctx.romain && !ctx.romain.isEmpty) {
    for (const metric of ctx.romain.metrics) {
      for (const r of ctx.romain.rows) {
        const cell = r.cells[metric.key];
        const d: DeltaValue | undefined =
          cell.prevValue == null
            ? undefined
            : {
                value: cell.value,
                prevValue: cell.prevValue,
                deltaAbs: cell.value - cell.prevValue,
                deltaPct:
                  cell.prevValue === 0
                    ? cell.value === 0
                      ? 0
                      : Infinity
                    : ((cell.value - cell.prevValue) / cell.prevValue) * 100,
                n1Unavailable: false,
              };
        push('Tableau de Romain', `${metric.label} — ${r.label}`, fmtNum(cell.value), d);
      }
    }
  }

  return rows;
}

/** Nom de fichier CSV du dashboard (édition + plage de dates, sans espaces). */
export function dashboardCsvFilename(ctx: { editionName: string; rangeLabel: string }): string {
  const slug = (s: string) =>
    s
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '') // supprime les diacritiques combinants
      .replace(/[^a-zA-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase();
  return `stats_${slug(ctx.editionName)}_${slug(ctx.rangeLabel)}`;
}
